import { compose, pick, spread, values } from 'lodash/fp';
import { Children, isValidElement } from 'react';

import { PartialDeep } from '@optimus-web/common';

import { overflowHandler, sizerStyleHandler } from './handlers';
import { Child, ViewportCalculationOptions } from './types';

export const createChildrenArray = (children: React.ReactNode): Array<Child> => {
    const mappedChildren = Children.map(children, (child, index) => {
        if (!isValidElement(child)) {
            return { key: index as React.Key, child: child as React.ReactChild };
        }
        return { key: child.key || index, child };
    });

    return Children.count(children) > 0 ? (mappedChildren as Array<Child>) : [];
};

export function calculateViewportProps(
    items: Child[],
    itemSizeCache: Map<string | number | bigint, number>,
    { startPosition, endPosition, defaultItemSize }: ViewportCalculationOptions
) {
    let index = 0;
    let offset = 0;
    let lastOffset = 0;

    let paddingSize = 0;
    let startIndex = 0;
    let endIndex = 0;
    let startIndexSet = false;

    // iterate until sum size of viewed items exceeds the end of visible area
    while (offset < endPosition) {
        lastOffset = offset;

        // get size of current item
        const { key = '' } = items[index] || {};

        let measuredSize = itemSizeCache.get(key);

        if (typeof measuredSize !== 'number') {
            measuredSize = defaultItemSize;
        }
        offset += measuredSize;

        // check if it's a first visible item
        if (offset >= startPosition && !startIndexSet) {
            // size of skipped space before first rendered item
            paddingSize = lastOffset;

            startIndex = index;
            startIndexSet = true;
        }

        // check if it's a last visible item
        if (offset >= endPosition) {
            endIndex = index;
        }

        index += 1;
    }

    return {
        paddingSize,
        startIndex,
        endIndex,
    };
}

export const getStyle = <ElementProps>(
    styleHandler: typeof sizerStyleHandler | typeof overflowHandler,
    ...handlerArgs: string[]
) =>
    compose<ElementProps, string, string[], PartialDeep<ElementProps>>(spread(styleHandler), values, pick(handlerArgs));
